import React, { useState, useEffect, useCallback } from 'react';
import {
    Person,
    Email,
    Phone,
    Business,
    Edit,
    Save,
    Badge,
    CheckCircle
} from '@mui/icons-material';
import CashierSidebar from '../../components/cashier/CashierSidebar';
import CashierHeader from '../../components/cashier/CashierHeader';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile } from '../../services/userRoleService';
import { fetchCaissierTerminal } from '../../services/posService';
import { hosts } from '../../config/environment';

const getAvatarUrl = (userId) => (userId && hosts.api)
    ? `${hosts.api}/exposed/v1/mda/images/show?target=avatar&target_id=${userId}`
    : null;

const SectionCard = ({ title, icon: Icon, children, delay = 0, iconClass = 'from-amber-500 to-orange-500 shadow-amber-500/25 ring-amber-500/20' }) => (
    <div
        className="bg-white/95 backdrop-blur-sm rounded-3xl border border-slate-200/80 shadow-xl shadow-slate-200/30 p-6 lg:p-8 hover:shadow-2xl hover:border-slate-300/60 transition-all duration-500 hover:-translate-y-1"
        style={{
            animation: `profileFadeIn 0.6s ease-out ${delay}ms both`
        }}
    >
        <div className="flex items-center gap-4 mb-6">
            <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${iconClass} flex items-center justify-center shadow-lg ring-2`}>
                <Icon className="text-white text-xl" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 tracking-tight">{title}</h2>
        </div>
        <div className="space-y-1">{children}</div>
    </div>
);

const InfoRow = ({ label, value, icon: Icon }) => (
    <div className="py-4 px-4 rounded-xl hover:bg-slate-50/80 transition-all duration-200 border-b border-slate-100 last:border-b-0 group">
        <div className="flex items-center gap-2 mb-1">
            {Icon && <Icon className="text-sky-500 text-base group-hover:text-sky-600 transition-colors" />}
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        </div>
        <p className="text-base font-semibold text-slate-800 ml-6">{value || <span className="text-slate-400 italic">Non renseigné</span>}</p>
    </div>
);

const CashierProfilePage = () => {
    const { user, selectedOrganization } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [caissierTerminal, setCaissierTerminal] = useState(null);
    const [caissierTerminalLoading, setCaissierTerminalLoading] = useState(false);
    const [openEditForm, setOpenEditForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [profile, setProfile] = useState({
        firstName: user?.profile?.firstName || '',
        lastName: user?.profile?.lastName || '',
        gender: user?.profile?.gender || 'MALE',
        completeName: user?.profile?.completeName || ''
    });

    const loadCaissierTerminal = useCallback(async () => {
        if (!selectedOrganization?.id || !user?.id) return;
        try {
            setCaissierTerminalLoading(true);
            const terminal = await fetchCaissierTerminal(selectedOrganization.id, user.id);
            setCaissierTerminal(terminal || null);
        } catch (error) {
            setCaissierTerminal(null);
        } finally {
            setCaissierTerminalLoading(false);
        }
    }, [selectedOrganization?.id, user?.id]);

    useEffect(() => {
        if (selectedOrganization?.id && user?.id) loadCaissierTerminal();
    }, [loadCaissierTerminal, selectedOrganization?.id, user?.id]);

    useEffect(() => {
        if (user?.profile) {
            setProfile({
                firstName: user.profile.firstName || '',
                lastName: user.profile.lastName || '',
                gender: user.profile.gender || 'MALE',
                completeName: user.profile.completeName || ''
            });
        }
    }, [user?.profile]);

    useEffect(() => {
        const completeName = `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
        setProfile(prev => ({ ...prev, completeName }));
    }, [profile.firstName, profile.lastName]);

    const handleSaveProfile = async () => {
        if (!user?.id) return;
        try {
            setSaving(true);
            await updateUserProfile(user.id, profile);
            setOpenEditForm(false);
            window.location.reload();
        } catch (error) {
            console.error('Error saving profile:', error);
            alert(error?.message || 'Erreur lors de la mise à jour du profil');
        } finally {
            setSaving(false);
        }
    };

    const displayName = profile?.completeName?.trim() || user?.name || user?.username || 'Utilisateur';
    const avatarUrl = getAvatarUrl(user?.id);
    const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(displayName)}&background=0ea5e9&color=fff`;
    const isActive = user?.enabled !== false;

    return (
        <>
            <style>{`
                @keyframes profileFadeIn {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes profilePulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(14, 165, 233, 0.3); }
                    50% { box-shadow: 0 0 0 16px rgba(14, 165, 233, 0); }
                }
                @keyframes profileShine {
                    0% { transform: translateX(-100%) skewX(-15deg); }
                    100% { transform: translateX(200%) skewX(-15deg); }
                }
            `}</style>
            <div className="min-h-screen bg-white flex">
                <CashierSidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

                <div className="flex-1 min-h-screen transition-all duration-300 overflow-hidden flex flex-col lg:ml-[var(--cashier-sidebar-w,18rem)] transition-[margin] duration-300">
                    <div className="bg-gradient-to-br from-slate-50 via-white to-sky-50/30 min-h-screen flex flex-col flex-1">
                        <CashierHeader
                            onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
                            terminal={caissierTerminal}
                            loading={caissierTerminalLoading}
                        />

                        <div className="flex-1 p-4 lg:p-8 overflow-y-auto">
                            {/* Hero */}
                            <div
                                className="relative rounded-3xl overflow-hidden mb-8 border border-slate-200/80 shadow-xl"
                                style={{ animation: 'profileFadeIn 0.6s ease-out 0ms both' }}
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-sky-500/10 via-white to-cyan-500/10 pointer-events-none" />
                                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(14,165,233,0.15),transparent)] pointer-events-none" />
                                <div className="relative px-6 lg:px-10 py-8 lg:py-10 flex flex-col sm:flex-row items-center sm:items-start gap-6">
                                    <div className="relative flex-shrink-0">
                                        <div className="absolute inset-0 bg-gradient-to-br from-sky-400 to-cyan-500 rounded-full blur-xl opacity-30 animate-[profilePulse_3s_ease-in-out_infinite]" />
                                        <div className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-full border-4 border-white shadow-2xl ring-4 ring-sky-100 overflow-hidden bg-gradient-to-br from-sky-500 to-cyan-500">
                                            <img
                                                src={avatarUrl || fallbackAvatar}
                                                alt=""
                                                className="w-full h-full object-cover"
                                                onError={(e) => { e.target.src = fallbackAvatar; }}
                                            />
                                        </div>
                                        <div className="absolute -bottom-0.5 -right-0.5 w-7 h-7 bg-emerald-500 rounded-full border-2 border-white shadow flex items-center justify-center">
                                            <div className="w-2 h-2 bg-white rounded-full" />
                                        </div>
                                    </div>
                                    <div className="flex-1 text-center sm:text-left">
                                        <h1 className="text-2xl lg:text-3xl font-bold text-slate-800 tracking-tight mb-2">
                                            {displayName}
                                        </h1>
                                        <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
                                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border ${isActive ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                                                <CheckCircle className="text-base" />
                                                Compte {isActive ? 'Actif' : 'Inactif'}
                                            </span>
                                            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-sky-50 text-sky-700 border border-sky-200">
                                                <Badge className="text-base" />
                                                Caissier
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                                {/* Identité & contact */}
                                <SectionCard title="Identité & contact" icon={Person} delay={100} iconClass="from-sky-500 to-cyan-500 shadow-sky-500/25 ring-sky-500/20">
                                    <InfoRow label="Email" value={typeof user?.email === 'string' ? user.email : user?.email?.address} icon={Email} />
                                    <InfoRow label="Téléphone" value={user?.phoneNumber || user?.phone?.fullNumber || user?.username} icon={Phone} />
                                    <InfoRow label="Rôle" value="Caissier" icon={Badge} />
                                    <InfoRow label="Organisation" value={selectedOrganization?.name} icon={Business} />
                                </SectionCard>

                                {/* Mon profil (éditable) */}
                                <SectionCard title="Mon profil" icon={Edit} delay={200} iconClass="from-amber-500 to-orange-500 shadow-amber-500/25 ring-amber-500/20">
                                    {!openEditForm ? (
                                        <>
                                            <InfoRow label="Prénom" value={profile.firstName || '—'} />
                                            <InfoRow label="Nom" value={profile.lastName || '—'} />
                                            <InfoRow label="Genre" value={profile.gender === 'MALE' ? 'Homme' : profile.gender === 'FEMALE' ? 'Femme' : '—'} />
                                            <div className="pt-4">
                                                <button
                                                    type="button"
                                                    onClick={() => window.open(`${hosts.accounts}/`, '_blank', 'noopener,noreferrer')}
                                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-amber-700 bg-amber-50 border border-amber-200 hover:bg-amber-100 hover:border-amber-300 transition-all duration-200 shadow-sm"
                                                >
                                                    <Edit className="text-lg" />
                                                    Modifier le profil
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-1">Prénom</label>
                                                <input
                                                    type="text"
                                                    value={profile.firstName || ''}
                                                    onChange={(e) => setProfile(prev => ({ ...prev, firstName: e.target.value }))}
                                                    placeholder="Votre prénom"
                                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all bg-slate-50 focus:bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-1">Nom</label>
                                                <input
                                                    type="text"
                                                    value={profile.lastName || ''}
                                                    onChange={(e) => setProfile(prev => ({ ...prev, lastName: e.target.value }))}
                                                    placeholder="Votre nom"
                                                    className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-400 focus:border-amber-400 transition-all bg-slate-50 focus:bg-white"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-slate-700 mb-2">Genre</label>
                                                <div className="flex gap-3">
                                                    {['MALE', 'FEMALE'].map((g) => (
                                                        <button
                                                            key={g}
                                                            type="button"
                                                            onClick={() => setProfile(prev => ({ ...prev, gender: g }))}
                                                            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-medium transition-all ${
                                                                profile.gender === g
                                                                    ? 'border-amber-500 bg-amber-50 text-amber-800'
                                                                    : 'border-slate-200 text-slate-600 hover:border-amber-300 hover:bg-amber-50/50'
                                                            }`}
                                                        >
                                                            {g === 'MALE' ? 'Homme' : 'Femme'}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="flex gap-3 pt-2">
                                                <button
                                                    type="button"
                                                    onClick={handleSaveProfile}
                                                    disabled={saving}
                                                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all disabled:opacity-60"
                                                >
                                                    {saving ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save className="text-lg" />}
                                                    {saving ? 'Enregistrement…' : 'Enregistrer'}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setOpenEditForm(false)}
                                                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-all"
                                                >
                                                    Annuler
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </SectionCard>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CashierProfilePage;
